<!--
  DocumentChatPanel (Spec 128, Epic #524) — free-floating, non-modal chat window at a document.
  Title bar drags, lower right corner resizes (Pointer Events); position and size are remembered
  in localStorage (every access in try/catch) and always clamped to the visible area (E19b).
  Below 640 px the window fills the screen and is not draggable (E19c).
  The parent owns the open state and the `?chat=1` URL flag; this component reports `close`.
-->
<script lang="ts">
  import { createEventDispatcher, onMount, tick } from 'svelte';
  import { renderChatMarkdown } from '../utils/chatMarkdown';
  import { _ } from 'svelte-i18n';
  import { FileText, Plus, Send, RotateCcw, Sparkles, X } from 'lucide-svelte';
  import { api } from '../api';
  import type {
    AvailableSkill,
    ChatFollowup,
    ChatMessage,
    ChatSessionSummary,
    FollowUpTemplate,
  } from '../api/chat';
  import { ApiError } from '../api/errorHandler';
  import { formatDate, formatDateTime } from '../settings';
  import { userSettings } from '../stores/settingsStore';
  import { toastStore } from '../stores/toastStore';
  import ChatDeadlineHead from './ChatDeadlineHead.svelte';
  import {
    AiDisclaimer,
    Badge,
    Button,
    CopyIconButton,
    DateInput,
    Modal,
    Spinner,
  } from './ui/index';

  export let documentId: string;
  export let displayName: string;
  /** Extracted values the AI receives (E5), already without system fields. */
  export let contextValues: [string, string][] = [];
  export let ocrLength = 0;
  export let messages: ChatMessage[] = [];
  /** Skills that apply to the document; offered before the base suggestions in the empty history (Spec 129 E5a). */
  export let availableSkills: AvailableSkill[] = [];
  /** State of the history load owned by the parent: shows a spinner or an error with "Neu laden". */
  export let status: 'loading' | 'ready' | 'error' = 'ready';
  /** Spec 143: the session the messages belong to; `null` is the new, not yet started session (E6). Owned by the parent so it survives closing the window. */
  export let sessionId: string | null = null;
  /** Spec 145 E6: due date (ISO) of the page's active follow-up; an offer for exactly this date is shown as done. The page changes it (also on accepting here, via `followupCreated`). */
  export let activeFollowupDueDate: string | null = null;
  /** Spec 143 E4: the sessions of the document, last used first, as the server sorts them. */
  export let sessions: ChatSessionSummary[] = [];

  const dispatch = createEventDispatcher<{
    close: void;
    reload: void;
    followupCreated: ChatFollowup;
  }>();

  const STORAGE_KEY = 'plc.chat.window';
  const DEFAULT_SIZE = { width: 420, height: 600 };
  const MIN_SIZE = { width: 320, height: 400 };
  const EDGE_GAP = 24;
  // Room for the page header (title, breadcrumbs, action buttons) above the default window.
  const TOP_RESERVE = 136;
  const FULLSCREEN_BELOW = 640;
  const SUGGESTION_KEYS = [
    'chat.suggestion_summarize',
    'chat.suggestion_explain',
    'chat.suggestion_deadlines',
  ];

  // Spec 130 (skill deadline) or Spec 145 (offer of an answer): exactly one of the two, as the endpoint requires (E7).
  type FollowupTarget = { deadlineId: string } | { messageId: string };

  type Frame = { left: number; top: number; width: number; height: number };

  let viewport = { width: window.innerWidth, height: window.innerHeight };
  let frame: Frame = initialFrame();
  let input = '';
  let insertedSuggestion = '';
  let sending = false;
  // Spec 143: a session is being loaded after the user picked it; `switchToken` drops an answer that is no longer the newest request.
  let switching = false;
  let switchToken = 0;
  let pendingText = '';
  let popoverOpen = false;
  // Spec 130: the template that was just copied and the deadline waiting for the answer to "Bestehende Wiedervorlage ersetzen?".
  let copiedMessageId: string | null = null;
  let replaceTarget: FollowupTarget | null = null;
  let followupBusy = false;
  // Spec 133 E10: the reply deadline the user picked per template message (ISO); never sent to the server.
  let replyDates: Record<string, string> = {};
  let listEl: HTMLElement | undefined;
  let inputEl: HTMLTextAreaElement | undefined;

  // Spec 143 E7a/E13: the select replaces the title once there is a session; "+" is visible but off while the window already is a new session.
  $: newSessionShown = sessionId === null;
  $: sessionChangeLocked = sending || switching || status !== 'ready';
  $: fullscreen = viewport.width < FULLSCREEN_BELOW;
  $: style = fullscreen
    ? 'left: 0px; top: 0px; width: 100vw; height: 100dvh;'
    : `left: ${frame.left}px; top: ${frame.top}px; width: ${frame.width}px; height: ${frame.height}px;`;
  $: canSend = input.trim().length > 0 && !sending && !switching;
  $: if (messages || pendingText) scrollToEnd();
  // Spec 134 P4: the input gets the focus after the user's own action (send, suggestion, "Datum angeben"), never on loading a history.
  // Spec 134 E6/E10: "Datum angeben" sits only under the last closed question, as long as no new question followed.
  $: lastAbandonedIndex = messages.map((m) => m.kind).lastIndexOf('fact-abandoned');
  $: resumeOfferId =
    lastAbandonedIndex >= 0 &&
    !messages.slice(lastAbandonedIndex).some((m) => m.kind === 'fact-question')
      ? messages[lastAbandonedIndex].id
      : null;
  // Spec 135 E7: "Starten" sits only under the last skill offer, as long as no skill is active.
  $: lastSkillOfferIndex = messages.map((m) => m.kind).lastIndexOf('skill-offer');
  $: skillOfferId =
    lastSkillOfferIndex >= 0 &&
    availableSkills.some((s) => s.id === messages[lastSkillOfferIndex].skillId) &&
    !messages.some((m) => m.role === 'model' && m.skillId && m.kind !== 'skill-offer')
      ? messages[lastSkillOfferIndex].id
      : null;
  // Spec 146 E5/E7: next to it stand the follow-up templates of the active skill, as soon as a notice of defect is in the history.
  // Like the backend (DocumentChat.ActiveSkillMessage): the first model message with a skill that is not an offer.
  $: activeSkillId = messages.find(
    (m) => m.role === 'model' && m.skillId && m.kind !== 'skill-offer',
  )?.skillId;
  $: followUpTemplates = messages.some((m) => m.kind === 'template')
    ? (availableSkills.find((s) => s.id === activeSkillId)?.followUpTemplates ?? [])
    : [];
  // P4, Spec 146 E8: the offer ends with the first template after the last assessment, unless the skill has follow-up templates:
  // then "Mängelanzeige erstellen" stays at the latest assessment and the follow-up buttons stand next to it.
  $: lastAssessmentIndex = messages.map((m) => m.kind).lastIndexOf('assessment');
  $: templateOfferId =
    lastAssessmentIndex >= 0 &&
    (followUpTemplates.length > 0 ||
      !messages.slice(lastAssessmentIndex).some((m) => m.kind === 'template'))
      ? messages[lastAssessmentIndex].id
      : null;
  // Spec 148 E2: the article buttons of a device question stand only at the newest message; free text stays possible.
  $: newestMessage = messages[messages.length - 1];
  $: choicesMessageId = newestMessage?.choices?.length ? newestMessage.id : null;
  // Spec 146 E3, Spec 148 E5: the suggestion goes into an empty field once per question, only while that question is the newest message.
  let suggestedForId: string | null = null;
  $: offerSuggestion(messages);

  // Spec 141 E6b: "Datum ändern" sits only at the latest assessment, as long as no question followed it.
  $: changeDateOfferId =
    lastAssessmentIndex >= 0 &&
    !messages.slice(lastAssessmentIndex).some((m) => m.kind === 'fact-question')
      ? messages[lastAssessmentIndex].id
      : null;

  /** Spec 141 E11: the earliest reply deadline is tomorrow (the user's local day), as ISO. */
  function tomorrowIso(): string {
    const day = new Date();
    day.setDate(day.getDate() + 1);
    return [day.getFullYear(), day.getMonth() + 1, day.getDate()]
      .map((n, i) => String(n).padStart(i === 0 ? 4 : 2, '0'))
      .join('-');
  }

  const atLeastTomorrow = (iso: string): string =>
    iso && iso < tomorrowIso() ? tomorrowIso() : iso;

  /** E10: the date of the field if the user changed it, else the server's proposal (at least tomorrow, Spec 141 E11). */
  function replyIso(message: ChatMessage, dates: Record<string, string>): string {
    return message.id in dates ? dates[message.id] : atLeastTomorrow(message.replyDeadline ?? '');
  }

  /** E10: `{replyDeadline}` becomes the chosen date as dd.MM.yyyy in the shown and the copied text. */
  function letterText(message: ChatMessage, dates: Record<string, string>): string {
    if (message.kind !== 'template' || !message.replyDeadline) return message.text;
    const iso = atLeastTomorrow(replyIso(message, dates));
    // Fixed dd.MM.yyyy on purpose: a letter date like {handoverDate} from the backend, not the display setting (Spec 130 E17).
    const date = iso ? iso.split('-').reverse().join('.') : $_('chat.replyDeadlineMissing');
    return message.text.replaceAll('{replyDeadline}', date);
  }

  function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), Math.max(min, max));
  }

  /** Keeps the whole window inside the viewport; size shrinks first, then the position moves. */
  function fit(next: Frame): Frame {
    const width = clamp(next.width, Math.min(MIN_SIZE.width, viewport.width), viewport.width);
    const height = clamp(next.height, Math.min(MIN_SIZE.height, viewport.height), viewport.height);
    return {
      width,
      height,
      left: clamp(next.left, 0, viewport.width - width),
      top: clamp(next.top, 0, viewport.height - height),
    };
  }

  function defaultFrame(): Frame {
    const height = Math.max(
      MIN_SIZE.height,
      Math.min(DEFAULT_SIZE.height, viewport.height - TOP_RESERVE - EDGE_GAP),
    );
    return fit({
      width: DEFAULT_SIZE.width,
      height,
      left: viewport.width - DEFAULT_SIZE.width - EDGE_GAP,
      top: Math.max(viewport.height - height - EDGE_GAP, TOP_RESERVE),
    });
  }

  function initialFrame(): Frame {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null');
      const keys: (keyof Frame)[] = ['left', 'top', 'width', 'height'];
      if (stored && keys.every((k) => Number.isFinite(stored[k]))) return fit(stored as Frame);
    } catch {
      // storage unavailable or corrupt: fall through to the default position
    }
    return defaultFrame();
  }

  function persistFrame() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(frame));
    } catch {
      // not persisting is fine; the window still works
    }
  }

  // --- Pointer interaction (drag by title bar, resize by corner) ---

  let gesture: { kind: 'move' | 'resize'; x: number; y: number; start: Frame } | null = null;

  function beginGesture(kind: 'move' | 'resize', event: PointerEvent) {
    if (fullscreen || event.button > 0) return;
    gesture = { kind, x: event.clientX, y: event.clientY, start: { ...frame } };
    try {
      (event.currentTarget as Element).setPointerCapture?.(event.pointerId);
    } catch {
      // capture is a nicety; the events still bubble from the handle
    }
    event.preventDefault();
  }

  function moveGesture(event: PointerEvent) {
    if (!gesture) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    const { start } = gesture;
    frame =
      gesture.kind === 'move'
        ? fit({ ...start, left: start.left + dx, top: start.top + dy })
        : fit({ ...start, width: start.width + dx, height: start.height + dy });
  }

  function endGesture() {
    if (!gesture) return;
    gesture = null;
    persistFrame();
  }

  function handleResize() {
    viewport = { width: window.innerWidth, height: window.innerHeight };
    frame = fit(frame);
  }

  // --- Conversation ---

  async function scrollToEnd() {
    await tick();
    if (listEl) listEl.scrollTop = listEl.scrollHeight;
  }

  // Without a known session the server answers with the last used one (Spec 143 E5), which is the one the failed first message started.
  async function reloadHistory() {
    try {
      const history = await api.chat.getHistory(documentId, sessionId ?? undefined);
      messages = history.messages;
      sessionId = history.sessionId;
      await refreshSessions();
    } catch {
      // keep what is shown; the failed state was already announced by a toast
    }
  }

  async function refreshSessions() {
    try {
      sessions = await api.chat.getSessions(documentId);
    } catch {
      // the list is a nicety; the conversation works without it
    }
  }

  /** Spec 143 E6: only a state of the window — the session starts with its first message on the server. */
  function startNewSession() {
    if (sessionChangeLocked || newSessionShown) return;
    switchToken++;
    sessionId = null;
    messages = [];
    focusInput();
  }

  async function selectSession(event: Event) {
    const select = event.currentTarget as HTMLSelectElement;
    const id = select.value;
    if (!id || id === sessionId || sessionChangeLocked) return;
    const token = ++switchToken;
    switching = true;
    try {
      const history = await api.chat.getHistory(documentId, id);
      if (token !== switchToken) return;
      messages = history.messages;
      sessionId = history.sessionId;
      // A session that started after the list was loaded is not in it yet.
      if (!sessions.some((s) => s.id === sessionId)) await refreshSessions();
    } catch (error) {
      if (token !== switchToken) return;
      select.value = sessionId ?? '';
      if (errorCodeOf(error) === 'chat.session_not_found') {
        toastStore.error($_('chat.errorSessionNotFound'));
        await refreshSessions();
      } else {
        toastStore.error($_('chat.errorSessionLoad'));
      }
    } finally {
      if (token === switchToken) switching = false;
    }
  }

  // Spec 130 codes are read from the `errorCode` property: the wire error always carries it, and the component tests
  // reject plain objects. The Spec 128 branches below keep their `instanceof ApiError` check unchanged.
  function errorCodeOf(error: unknown): string | undefined {
    return (error as { errorCode?: string } | null)?.errorCode;
  }

  /** Spec 130: a fact answer that is an ISO date is shown in the user's date format. */
  function userText(message: ChatMessage): string {
    return message.kind === 'fact-answer' && /^\d{4}-\d{2}-\d{2}$/.test(message.text)
      ? formatDate(`${message.text}T00:00:00`, $userSettings)
      : message.text;
  }

  async function handleError(error: unknown) {
    const code = error instanceof ApiError ? error.errorCode : undefined;
    if (code === 'chat.ai_unavailable') {
      toastStore.error($_('chat.errorFailed'));
      await reloadHistory();
    } else if (code === 'chat.conflict') {
      // Another tab or person wrote at the same time (ADR-065 point 3): show their state.
      toastStore.error($_('chat.errorConflict'));
      await reloadHistory();
    } else if (code === 'chat.session_not_found') {
      toastStore.error($_('chat.errorSessionNotFound'));
      dispatch('reload');
    } else if (code === 'chat.daily_limit_reached') {
      toastStore.error($_('chat.errorDailyLimit'));
    } else if (code === 'chat.monthly_budget_reached') {
      toastStore.error($_('chat.errorMonthlyBudget'));
    } else {
      toastStore.error($_('chat.errorSend'));
    }
  }

  async function run(
    request: () => Promise<{ sessionId: string | null; messages: ChatMessage[] }>,
    text = '',
  ): Promise<boolean> {
    sending = true;
    pendingText = text;
    try {
      const history = await request();
      messages = history.messages;
      sessionId = history.sessionId;
      // The list is sorted by last use and a new session or its title appears with this answer.
      void refreshSessions();
      return true;
    } catch (error) {
      await handleError(error);
      return false;
    } finally {
      sending = false;
      pendingText = '';
    }
  }

  async function send(raw: string, skillId?: string) {
    const text = raw.trim();
    if (!text || sending || switching) return;
    const fromInput = text === input.trim();
    // A button click leaves the field alone, unless it still holds the untouched proposal of an earlier question (not typed by the user).
    // Read before the reply: the reply may already put the next proposal into the field.
    const staleSuggestion = !fromInput && insertedSuggestion !== '' && input === insertedSuggestion;
    const ok = await run(
      () => api.chat.postMessage(documentId, text, skillId, sessionId ?? undefined),
      text,
    );
    // A rejected message that was not stored (limits, validation) stays in the field for another try.
    const storedAsFailed = messages.some(
      (m) => m.role === 'user' && m.text === text && m.status === 'failed',
    );
    if ((fromInput || staleSuggestion) && (ok || storedAsFailed)) {
      input = '';
      insertedSuggestion = '';
      offerSuggestion(messages);
    }
    await tick();
    inputEl?.focus();
  }

  async function focusInput() {
    await tick();
    inputEl?.focus();
  }

  async function resumeQuestion(message: ChatMessage, label: string) {
    if (sending || !message.skillId) return;
    const skillId = message.skillId;
    await run(
      () => api.chat.resumeQuestion(documentId, label, skillId, sessionId ?? undefined),
      label,
    );
    await focusInput();
  }

  async function startOfferedSkill(message: ChatMessage) {
    const skill = availableSkills.find((s) => s.id === message.skillId);
    if (sending || !skill) return;
    await send(skill.name, skill.id);
  }

  async function requestTemplate() {
    if (sending) return;
    await run(
      () => api.chat.requestTemplate(documentId, $_('chat.templateButton'), sessionId ?? undefined),
      $_('chat.templateButton'),
    );
  }

  async function requestFollowUp(template: FollowUpTemplate) {
    if (sending || !activeSkillId) return;
    const skillId = activeSkillId;
    await run(
      () =>
        api.chat.requestFollowUpTemplate(
          documentId,
          template.label,
          skillId,
          template.id,
          sessionId ?? undefined,
        ),
      template.label,
    );
  }

  // Not focused on purpose (UI/UX review): the text is a proposal the user may take or overwrite.
  function offerSuggestion(list: ChatMessage[]) {
    const newest = list[list.length - 1];
    if (!newest?.inputSuggestion || newest.id === suggestedForId || input.trim() !== '') return;
    // Only an inserted suggestion counts as offered: the answer to the previous question may still stand in the field (send empties it afterwards).
    suggestedForId = newest.id;
    input = newest.inputSuggestion;
    insertedSuggestion = input;
  }

  async function copyTemplate(message: ChatMessage) {
    try {
      // A case hint ("limitation period ended on …") stands before the letter, up to this separator; it is not part of the copy.
      const letter = letterText(message, replyDates);
      await navigator.clipboard.writeText(letter.split('\n\n---\n\n').pop() ?? letter);
      copiedMessageId = message.id;
      toastStore.success($_('chat.templateCopied'));
      setTimeout(() => (copiedMessageId = null), 2000);
    } catch {
      toastStore.error($_('chat.errorCopy'));
    }
  }

  async function createFollowup(target: FollowupTarget, replace = false) {
    if (!sessionId) return;
    followupBusy = true;
    try {
      const created = await api.chat.createFollowup(documentId, target, replace, sessionId);
      dispatch('followupCreated', created);
      replaceTarget = null;
      toastStore.success($_('chat.followupCreated'));
    } catch (error) {
      const code = errorCodeOf(error);
      if (code === 'followup.active_exists' && !replace) {
        replaceTarget = target;
      } else {
        replaceTarget = null;
        toastStore.error(
          code === 'chat.no_deadline_to_follow'
            ? $_('chat.errorNoDeadline')
            : $_('chat.errorFollowup'),
        );
      }
    } finally {
      followupBusy = false;
    }
  }

  // Esc also reaches the window handler below; the modal reacts first, so closing is deferred
  // to keep this Esc from closing the chat window as well.
  function closeReplaceModal() {
    setTimeout(() => (replaceTarget = null), 0);
  }

  async function retry(message: ChatMessage) {
    if (sending || !sessionId) return;
    const session = sessionId;
    await run(() => api.chat.retryMessage(documentId, message.id, session), message.text);
  }

  function handleInputKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;
    event.preventDefault();
    send(input);
  }

  // Window-wide, so Esc also works when the focus is on the page behind (E19f).
  function handleWindowKeydown(event: KeyboardEvent) {
    if (event.key !== 'Escape' || event.defaultPrevented || replaceTarget !== null) return;
    if (popoverOpen) {
      popoverOpen = false;
    } else {
      dispatch('close');
    }
  }

  onMount(() => {
    window.addEventListener('resize', handleResize);
    inputEl?.focus();
    return () => window.removeEventListener('resize', handleResize);
  });
</script>

<svelte:window on:keydown={handleWindowKeydown} />

<div
  data-testid="chat-window"
  data-fullscreen={fullscreen ? 'true' : 'false'}
  role="dialog"
  aria-modal="false"
  aria-label={$_('chat.title')}
  tabindex="-1"
  class="fixed z-50 flex flex-col overflow-hidden bg-surface border border-border shadow-card {fullscreen
    ? ''
    : 'rounded-xl'}"
  {style}
>
  <!-- Title bar: drag handle -->
  <div
    data-testid="chat-window-titlebar"
    role="presentation"
    class="flex items-center justify-between gap-2 px-4 h-11 flex-shrink-0 border-b border-border bg-bg-alt select-none touch-none {fullscreen
      ? ''
      : 'cursor-move'}"
    on:pointerdown={(e) => beginGesture('move', e)}
    on:pointermove={moveGesture}
    on:pointerup={endGesture}
    on:pointercancel={endGesture}
  >
    {#if sessions.length > 0}
      <label class="sr-only" for="chat-session-select">{$_('chat.sessionSelectLabel')}</label>
      <select
        id="chat-session-select"
        data-testid="chat-session-select"
        class="flex-1 min-w-0 h-8 px-2 text-sm font-semibold text-text bg-surface border border-border rounded cursor-pointer"
        value={sessionId ?? ''}
        disabled={sessionChangeLocked}
        on:pointerdown|stopPropagation={() => {}}
        on:change={selectSession}
      >
        {#if newSessionShown}
          <option value="">{$_('chat.newSession')}</option>
        {/if}
        {#each sessions as session (session.id)}
          <option value={session.id}>{session.title}</option>
        {/each}
      </select>
    {:else}
      <h2 class="flex-1 min-w-0 truncate text-sm font-semibold text-text">{$_('chat.title')}</h2>
    {/if}
    <button
      type="button"
      data-testid="chat-new-session-button"
      class="inline-flex items-center justify-center w-8 h-8 text-text-muted hover:text-text bg-transparent border-none cursor-pointer rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      title={$_('chat.newSession')}
      aria-label={$_('chat.newSession')}
      disabled={sessionChangeLocked || newSessionShown}
      on:pointerdown|stopPropagation={() => {}}
      on:click={startNewSession}
    >
      <Plus class="w-4 h-4" strokeWidth={1.5} />
    </button>
    <button
      type="button"
      class="inline-flex items-center justify-center w-8 h-8 -mr-2 text-text-muted hover:text-text bg-transparent border-none cursor-pointer rounded transition-colors"
      aria-label={$_('chat.close')}
      on:pointerdown|stopPropagation={() => {}}
      on:click={() => dispatch('close')}
    >
      <X class="w-4 h-4" strokeWidth={1.5} />
    </button>
  </div>

  <!-- Messages (answers are announced politely, S25) -->
  <div
    bind:this={listEl}
    data-testid="chat-live-region"
    aria-live="polite"
    class="flex-1 overflow-y-auto px-4 py-3 space-y-3"
  >
    {#if status === 'loading' || switching}
      <div data-testid="chat-history-loading" class="flex justify-center py-8">
        <Spinner size="sm" label={$_('messages.loading')} />
      </div>
    {:else if status === 'error'}
      <div
        data-testid="chat-history-error"
        class="flex flex-col items-center gap-3 py-8 text-center"
      >
        <p class="text-sm text-error">{$_('chat.loadError')}</p>
        <Button variant="secondary" size="sm" on:click={() => dispatch('reload')}>
          <RotateCcw class="w-4 h-4" strokeWidth={1.5} />
          {$_('chat.reload')}
        </Button>
      </div>
    {:else if messages.length === 0 && !sending}
      <div class="flex flex-col items-center gap-3 py-8 text-center">
        <p class="text-sm font-medium text-text-secondary">{$_('chat.emptyTitle')}</p>
        <div class="flex flex-col gap-2 w-full">
          {#each availableSkills as skill (skill.id)}
            <button
              type="button"
              data-testid="chat-skill-suggestion-{skill.id}"
              class="inline-flex items-center gap-2 w-full px-3 py-2 text-sm text-left text-accent bg-accent/10 border border-accent/40 rounded-lg cursor-pointer hover:bg-accent/[0.14] transition-colors"
              on:click={() => send(skill.name, skill.id)}
            >
              <Sparkles class="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
              {skill.name}
            </button>
          {/each}
          {#each SUGGESTION_KEYS as key, i (key)}
            <button
              type="button"
              data-testid="chat-suggestion-{i}"
              class="w-full px-3 py-2 text-sm text-left text-text bg-surface border border-border rounded-lg cursor-pointer hover:bg-surface-hover transition-colors"
              on:click={() => send($_(key))}
            >
              {$_(key)}
            </button>
          {/each}
        </div>
      </div>
    {/if}

    {#each messages as message (message.id)}
      {#if message.role === 'user'}
        <div class="flex flex-col items-end gap-1">
          <p
            class="max-w-[85%] px-3 py-2 text-sm text-on-accent bg-accent rounded-lg whitespace-pre-wrap break-words"
          >
            {userText(message)}
          </p>
          <span class="text-xs text-text-muted">
            {message.authorDisplayName ?? ''} · {formatDateTime(message.postedAt, $userSettings)}
          </span>
          {#if message.status === 'failed'}
            <span data-testid="chat-message-failed-{message.id}" class="text-xs text-error">
              {$_('chat.statusFailed')}
            </span>
            <Button
              variant="secondary"
              size="sm"
              disabled={sending}
              data-testid="chat-retry-button-{message.id}"
              on:click={() => retry(message)}
            >
              <RotateCcw class="w-4 h-4" strokeWidth={1.5} />
              {$_('chat.retry')}
            </Button>
          {/if}
        </div>
      {:else}
        <div class="flex flex-col items-start gap-1">
          <span data-testid="chat-ai-badge"
            ><Badge variant="accent">{$_('chat.aiBadge')}</Badge></span
          >
          {#if message.kind === 'assessment' && message.deadlineHead}
            <!-- Spec 133 E8: the card is derived at read time; the stored Markdown text is for the AI history and the export. -->
            <ChatDeadlineHead
              messageId={message.id}
              head={message.deadlineHead}
              busy={followupBusy || sending}
              canChangeDate={message.id === changeDateOfferId}
              on:followup={(e) => createFollowup({ deadlineId: e.detail.deadlineId })}
              on:changedate={() => resumeQuestion(message, $_('chat.changeDateButton'))}
            />
          {:else}
            <div
              class="max-w-[85%] px-3 py-2 text-sm text-text bg-bg-alt border border-border rounded-lg break-words space-y-2"
            >
              {#if message.kind === 'template' && message.replyDeadline}
                <div class="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <label
                    for="chat-reply-deadline-{message.id}"
                    class="text-xs text-text-secondary shrink-0"
                  >
                    {$_('chat.replyDeadlineLabel')}
                  </label>
                  <DateInput
                    id="chat-reply-deadline-{message.id}"
                    class="sm:w-auto"
                    min={tomorrowIso()}
                    value={replyIso(message, replyDates)}
                    on:change={(e) => (replyDates = { ...replyDates, [message.id]: e.detail })}
                  />
                </div>
              {/if}
              <!-- Spec 131 E1: renderChatMarkdown escapes all HTML characters before converting the Markdown subset. -->
              <!-- eslint-disable-next-line svelte/no-at-html-tags -->
              {@html renderChatMarkdown(letterText(message, replyDates))}
            </div>
          {/if}
          {#if message.truncated}
            <span class="text-xs text-text-secondary">{$_('chat.truncated')}</span>
          {/if}
          {#if message.kind === 'template'}
            <CopyIconButton
              copied={copiedMessageId === message.id}
              label={$_('chat.templateCopy')}
              copiedLabel={$_('chat.templateCopied')}
              data-testid="chat-template-copy-{message.id}"
              on:click={() => copyTemplate(message)}
            />
          {:else if message.kind === 'assessment' && message.id === templateOfferId}
            <div class="flex flex-wrap gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={sending}
                data-testid="chat-template-button"
                on:click={requestTemplate}
              >
                {$_('chat.templateButton')}
              </Button>
              {#each followUpTemplates as template (template.id)}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={sending}
                  data-testid="chat-followup-template-{template.id}"
                  on:click={() => requestFollowUp(template)}
                >
                  {template.label}
                </Button>
              {/each}
            </div>
          {:else if message.id === skillOfferId}
            <Button
              variant="secondary"
              size="sm"
              disabled={sending}
              data-testid="chat-skill-offer-start"
              on:click={() => startOfferedSkill(message)}
            >
              {$_('chat.startSkillButton')}
            </Button>
          {:else if message.id === resumeOfferId}
            <Button
              variant="secondary"
              size="sm"
              disabled={sending}
              data-testid="chat-resume-button"
              on:click={() => resumeQuestion(message, $_('chat.resumeButton'))}
            >
              {$_('chat.resumeButton')}
            </Button>
          {/if}
          {#if message.id === choicesMessageId}
            <div
              class="flex flex-wrap gap-2"
              role="group"
              aria-label={message.text}
              data-testid="chat-choices"
            >
              {#each message.choices ?? [] as choice (choice)}
                <Button
                  variant="secondary"
                  size="sm"
                  class="h-auto! min-h-8 max-w-full py-1 text-left"
                  disabled={sending}
                  on:click={() => send(choice)}
                >
                  {choice}
                </Button>
              {/each}
            </div>
          {/if}
          {#if message.followupOffer}
            <!-- Spec 145 E6: the date of the answer as a follow-up; done while the document's active follow-up has exactly this date. -->
            <div class="flex flex-wrap gap-2">
              {#if activeFollowupDueDate === message.followupOffer}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled
                  data-testid="chat-followup-offer-done"
                >
                  {$_('chat.followupOfferDone')}
                </Button>
              {:else}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={followupBusy || sending}
                  data-testid="chat-followup-offer-button"
                  on:click={() => createFollowup({ messageId: message.id })}
                >
                  {$_('chat.followupOfferButton', {
                    values: {
                      date: formatDate(`${message.followupOffer}T00:00:00`, $userSettings),
                    },
                  })}
                </Button>
              {/if}
            </div>
          {/if}
        </div>
      {/if}
    {/each}

    {#if sending}
      {#if pendingText}
        <div class="flex justify-end">
          <p
            class="max-w-[85%] px-3 py-2 text-sm text-on-accent bg-accent/70 rounded-lg whitespace-pre-wrap break-words"
          >
            {pendingText}
          </p>
        </div>
      {/if}
      <div data-testid="chat-spinner" class="flex items-center gap-2 text-text-muted">
        <Spinner size="sm" label={$_('chat.thinking')} />
      </div>
    {/if}
  </div>

  <!-- Context chip + popover (E19d) -->
  <div class="relative flex-shrink-0 px-4 pt-2 pb-2">
    <button
      type="button"
      data-testid="chat-document-chip"
      class="inline-flex items-center gap-1.5 max-w-full px-2.5 h-7 text-xs text-text-secondary bg-bg-alt border border-border rounded-full cursor-pointer hover:bg-surface-hover transition-colors"
      aria-expanded={popoverOpen}
      aria-controls="chat-context-popover"
      aria-label="{$_('chat.documentChipLabel')}: {displayName}"
      on:click={() => (popoverOpen = !popoverOpen)}
    >
      <FileText class="w-3.5 h-3.5 flex-shrink-0" strokeWidth={1.5} />
      <span class="truncate">{displayName}</span>
    </button>

    {#if popoverOpen}
      <div
        id="chat-context-popover"
        data-testid="chat-context-popover"
        class="absolute left-4 bottom-full mb-1 z-10 w-72 max-h-60 overflow-y-auto p-3 bg-surface border border-border rounded-lg shadow-card"
      >
        <p class="mb-2 text-xs font-semibold text-text">{$_('chat.contextPopoverTitle')}</p>
        <ul class="space-y-1 text-xs text-text-secondary">
          {#each contextValues as [name, value] (name)}
            <li data-testid="chat-context-item">
              <span class="font-medium">{name}:</span>
              {value}
            </li>
          {/each}
          {#if ocrLength > 0}
            <li data-testid="chat-context-item">
              {$_('chat.ocrChars', { values: { count: ocrLength } })}
            </li>
          {/if}
          {#if contextValues.length === 0 && ocrLength === 0}
            <li data-testid="chat-context-item">{$_('chat.contextFileNameOnly')}</li>
          {/if}
        </ul>
      </div>
    {/if}
  </div>

  <!-- AI notice (E15) and input -->
  <div data-testid="chat-ai-notice" class="flex-shrink-0 px-2">
    <AiDisclaimer
      text={$_('chat.aiNotice')}
      info={$_('chat.aiNoticeInfo')}
      class="text-text-secondary"
    />
  </div>
  <div class="flex items-end gap-2 flex-shrink-0 px-4 pb-4">
    <textarea
      bind:this={inputEl}
      bind:value={input}
      data-testid="chat-input"
      rows="2"
      maxlength="4000"
      disabled={sending || status === 'error'}
      aria-label={$_('chat.inputLabel')}
      placeholder={$_('chat.inputPlaceholder')}
      class="flex-1 resize-none px-3 py-2 text-sm text-text bg-bg border border-border rounded-lg focus:outline-none focus:border-accent disabled:opacity-60"
      on:keydown={handleInputKeydown}
    ></textarea>
    <Button
      variant="primary"
      size="sm"
      iconOnly
      disabled={!canSend}
      data-testid="chat-send-button"
      aria-label={$_('chat.send')}
      on:click={() => send(input)}
    >
      <Send class="w-4 h-4" strokeWidth={1.5} />
    </Button>
  </div>

  {#if !fullscreen}
    <div
      data-testid="chat-resize-handle"
      role="presentation"
      aria-label={$_('chat.resizeHandle')}
      class="absolute right-0 bottom-0 w-4 h-4 cursor-nwse-resize touch-none"
      on:pointerdown={(e) => beginGesture('resize', e)}
      on:pointermove={moveGesture}
      on:pointerup={endGesture}
      on:pointercancel={endGesture}
    ></div>
  {/if}
</div>

<Modal
  open={replaceTarget !== null}
  title={$_('chat.followupReplaceTitle')}
  on:close={closeReplaceModal}
>
  <p class="text-sm text-text-secondary">{$_('chat.followupReplaceInfo')}</p>
  <svelte:fragment slot="footer">
    <Button
      variant="secondary"
      size="sm"
      data-testid="chat-followup-replace-cancel"
      on:click={() => (replaceTarget = null)}
    >
      {$_('chat.followupReplaceCancel')}
    </Button>
    <Button
      variant="primary"
      size="sm"
      disabled={followupBusy}
      data-testid="chat-followup-replace-confirm"
      on:click={() => replaceTarget && createFollowup(replaceTarget, true)}
    >
      {$_('chat.followupReplaceConfirm')}
    </Button>
  </svelte:fragment>
</Modal>
